import { pick, findIndex, find, isEqual, each } from 'lodash'

const whitelistProperties = [
  'id',
  'name',
  'slug',
  'profile.tagline',
  'profile.highest_followers',
  'profile.highest_followers_icon',
  'profile_image_url'
]

// Shortlist state.
const state = {
  shortlist: [],
  version: window.storageVersion
}

// Shortlist state getters.
const getters = {
  haveNoShortlist: (state) => {
    return state.shortlist.length === 0
  },

  /**
   * Determine if we have a star in the shortlist.
   *
   * @return bool
   */
  hasStar: (state) => (star) => {
    return find(state.shortlist, existingStar => existingStar.id === star.id) !== undefined
  }
}

// Shortlist actions
const actions = {
  /**
   * Get the shortlist from the local storage.
   *
   * @return object
   */
  getStoredShortlist ({ commit, dispatch }) {
    let storedShortlist = window.store.get('shortlist')

    // If we have have no shortlist OR the version does not match we'll empty/remove
    // the shortlist. This might happen when we update something that has breaking
    // changes for existing shortlists.
    if (storedShortlist === undefined || storedShortlist.version !== window.storageVersion) {
      commit('SET_SHORTLIST', { shortlist: [], version: window.storageVersion })
      dispatch('removeStoredShortlist')
    } else {
      commit('SET_SHORTLIST', storedShortlist)
    }

    return state.shortlist
  },

  /**
   * Clear the stored shortlist.
   *
   * @return void
   */
  removeStoredShortlist () {
    window.store.remove('shortlist')
  },

  /**
   * Add a star to the shortlist.
   *
   * @return Promise
   */
  addStar ({ commit, dispatch, state }, star) {
    commit('ADD_SHORTLIST_STAR', pick(star, whitelistProperties))
    return Promise.resolve()
  },

  /**
   * Add a star to the shortlist.
   *
   * @return Promise
   */
  addStars ({ commit, dispatch, state }, stars) {
    each(stars, (star) => {
      commit('ADD_SHORTLIST_STAR', pick(star, whitelistProperties))
    })
  },
  /**
   * Remove a star from the shortlist.
   *
   * @return void
   */
  removeStar ({ commit, dispatch }, id) {
    commit('REMOVE_SHORTLIST_STAR', id)

    dispatch('storeShortlist')
  },

  /**
   * Set the shortlist.
   *
   * @return void
   */
  setShortlist ({ commit, state }, shortlist) {
    // IE does weird stuff with "storage" events so when setting the shortlist we need to double check
    // that the shortlist we are setting is not equal to the existing shortlist in state for this
    // browser. Most other browsers handle this a lot better.
    if (isEqual(state.shortlist, shortlist)) {
      return
    }
    commit('SET_SHORTLIST', { shortlist, version: state.version })
  },

  /**
   * Store the shortlist in storage.
   *
   * @return void
   */
  storeShortlist ({ commit, state }) {
  },

  /**
   * Clear the shortlist.
   *
   * @return void
   */
  clearShortlist ({ commit, dispatch }) {
    commit('SET_SHORTLIST', { version: window.storageVersion, shortlist: [] })

    dispatch('storeShortlist')
  }
}

// Shortlist state mutations.
const mutations = {
  SET_SHORTLIST (state, { version, shortlist }) {
    state.shortlist = shortlist
    state.version = version
  },

  ADD_SHORTLIST_STAR (state, star) {
    let existingStar = findIndex(state.shortlist, existing => existing.id === star.id)

    if (existingStar === -1) {
      state.shortlist.push(star)
    } else {
      state.shortlist.splice(existingStar, 1, star)
    }
  },

  REMOVE_SHORTLIST_STAR (state, id) {
    let existingStar = findIndex(state.shortlist, existing => existing.id === id)

    if (existingStar > -1) {
      state.shortlist.splice(existingStar, 1)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
